import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ClientOrgChipProps {
  /**
   * Resolved client display name (alias ?? org name). When omitted (the
   * admin-only lookup hasn't surfaced this client), a generic "Client" label is
   * shown so a non-admin coordinator still sees the provenance cue.
   */
  label?: string;
  className?: string;
}

/**
 * BLU-367/389 cross-org chip: a small brand-purple outline badge marking a row
 * that belongs to a Shopify-store client at a shared 3PL location (the row's
 * `organization_id` differs from the caller's). Rendered next to the row's
 * identity so a 3PL operator can tell whose order/issue they're acting on.
 *
 * This is the canonical form first proven on the Orders queue
 * (`ExpandableOrderRow`); extracted here so the production-issues surfaces reuse
 * the exact same visual instead of re-inventing it. Render ONLY for cross-org
 * rows — suppress on own-org (visual noise otherwise).
 *
 * Visual discipline (per `apps/frontend/DESIGN.md` §13): single brand-purple
 * accent, outline badge (no loud fill), tooltip carries the full explanation;
 * the label text itself communicates "client" so it's never info-only.
 */
export function ClientOrgChip({ label, className }: ClientOrgChipProps) {
  const { t } = useTranslation('production');
  const text = label ?? t('orders.clientOrderBadge', { defaultValue: 'Client' });
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A long client name ends in an ellipsis when the chip's container is
            narrower than the name (max-w-full + the truncating span); the
            tooltip shows it in full on hover. No `title`: the browser's own
            tooltip would show beside the Radix one. */}
        <Badge
          variant="outline"
          size="sm"
          className={cn(
            'max-w-full border-threadheads-purple/40 bg-threadheads-purple/5 text-threadheads-purple',
            className,
          )}
        >
          <span className="min-w-0 truncate">{text}</span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="break-words font-medium">{text}</p>
        <p>
          {t('orders.clientOrderTooltip', {
            defaultValue:
              'Order belongs to a Shopify-store client at this shared location.',
          })}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
